import { useState, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAppKitAccount } from '@reown/appkit/react';
import { useAccount } from 'wagmi';
import { useReadContract, useWriteContract, useWaitForTransactionReceipt, useBlockNumber } from 'wagmi';
import { contractAddress } from '../config/contract';
import { abi } from '../abi';
import { abiBSC } from '../abiBSC';
import { formatUnits } from 'viem';
import { supabase } from '../services/supabase';

interface UserStats {
  tokensStaked: number;
  poolPercentage: number;
  totalRewardEarned: number;
}

// Helper function to format large token values
const formatTokenAmount = (amount: string | number | bigint | undefined): string => {
  if (amount === undefined) return '0';
  
  // Convert to string if it's not already
  const amountStr = amount.toString();
  
  // Check if we're dealing with a wei value (very large number)
  if (amountStr.length > 10) {
    // Format from wei (18 decimals) to a readable number
    return Number(formatUnits(BigInt(amountStr), 18)).toFixed(2);
  }
  
  return amountStr;
};

const Staking = () => {
  const { address: user } = useAppKitAccount();
  const { chain } = useAccount();
  const networkKey = chain?.id === 56 ? 'bsc' : 'eth';
  const stakingContract = contractAddress[networkKey];
  
  // For block-based data refresh
  const { data: blockNumber } = useBlockNumber();

  // State for total staked values
  const [totalStakedETH, setTotalStakedETH] = useState<string>('0');
  const [totalStakedBSC, setTotalStakedBSC] = useState<string>('0');
  const [totalStakedSOL, setTotalStakedSOL] = useState<string>('0');
  const [combinedTotalStaked, setCombinedTotalStaked] = useState<string>('0');
  const [isLoadingTotals, setIsLoadingTotals] = useState<boolean>(true);

  // Check if using Solana wallet specifically
  const isSolanaWallet = !chain && !!user;
  
  // State for Solana values from Supabase
  const [solanaUserStaked, setSolanaUserStaked] = useState<string>('0');
  const [solanaPendingStake, setSolanaPendingStake] = useState<string>('0');
  const [isLoadingSolanaStake, setIsLoadingSolanaStake] = useState<boolean>(false);

  // Read user stats for the current network
  const { data: userStats, refetch: refetchStats, isLoading: isLoadingUserStats } = useReadContract({
    address: stakingContract,
    abi: networkKey === 'bsc' ? abiBSC : abi,
    functionName: 'getUserStats',
    args: [user ?? '0x0000000000000000000000000000000000000000'],
    query: {
      enabled: !!stakingContract && !!user,
      gcTime: 0, // Don't cache this data
      staleTime: 0, // Always consider data stale
    }
  }) as { data: UserStats | undefined; refetch: () => void; isLoading: boolean };

  // Read staking balance directly from the current network contract
  const { data: stakingBalance, isLoading: isLoadingStakingBalance } = useReadContract({
    address: stakingContract,
    abi: networkKey === 'bsc' ? abiBSC : abi,
    functionName: networkKey === 'bsc' ? 'stakeData' : 'stakingBalance',
    args: [user ?? '0x0000000000000000000000000000000000000000'],
    query: {
      enabled: !!stakingContract && !!user,
      gcTime: 0,
      staleTime: 0,
    }
  });

  // Helper to get the user's staked amount from any network
  const getUserStakedAmount = () => {
    // For Solana wallets - get data from our Supabase state
    if (isSolanaWallet) {
      console.log('Getting staked amount for Solana wallet:', solanaUserStaked);
      return solanaUserStaked;
    }
    
    console.log('Network:', networkKey);
    console.log('Staking balance raw:', stakingBalance);
    console.log('User stats raw:', userStats);
    
    // For BSC network - handle differently
    if (networkKey === 'bsc') {
      // BSC can return array or object format
      if (stakingBalance) {
        // If it's an array [pending, staked]
        if (Array.isArray(stakingBalance) && stakingBalance.length > 1) {
          const staked = stakingBalance[1];
          return formatTokenAmount(staked);
        }
        // If it's an object with staked property
        else if (typeof stakingBalance === 'object' && stakingBalance !== null) {
          const staked = (stakingBalance as any).staked;
          return staked ? formatTokenAmount(staked.toString()) : '0';
        }
      }
      
      // Fallback to userStats if available
      if (userStats && typeof userStats === 'object') {
        // Array format [depositedUSD, pending, staked, ...]
        if (Array.isArray(userStats) && userStats.length > 2) {
          return formatTokenAmount(userStats[2]);
        }
        // Object format with tokensStaked property
        else if ('tokensStaked' in userStats) {
          return formatTokenAmount(userStats.tokensStaked);
        }
      }
      
      return '0';
    }
    
    // For ETH network
    // First try to get from stakingBalance (more reliable direct mapping)
    if (stakingBalance) {
      return formatTokenAmount(stakingBalance.toString());
    }
    // Fall back to userStats if available
    else if (userStats?.tokensStaked) {
      return formatTokenAmount(userStats.tokensStaked);
    }
    // Default if neither is available
    return '0';
  };

  // Read pending purchase (stakeable) - also works for BSC
  const { data: pendingStake, isLoading: isLoadingPendingStake } = useReadContract({
    address: stakingContract,
    abi: networkKey === 'bsc' ? abiBSC : abi,
    functionName: networkKey === 'bsc' ? 'stakeData' : 'pendingPurchase',
    args: [user ?? '0x0000000000000000000000000000000000000000'],
    query: {
      enabled: !!stakingContract && !!user && !!chain,
      gcTime: 0,
      staleTime: 0,
    }
  }) as { data: number | undefined; isLoading: boolean };

  // Helper to get the user's pending (stakeable) tokens
  const getUserPendingAmount = () => {
    // For Solana wallets - get data from our Supabase state
    if (isSolanaWallet) {
      console.log('Getting pending amount for Solana wallet:', solanaPendingStake);
      return solanaPendingStake;
    }
    
    console.log('Pending raw:', pendingStake);
    
    // For BSC network
    if (networkKey === 'bsc') {
      // BSC returns an array or object from stakeData
      if (pendingStake) {
        // If it's an array [pending, staked]
        if (Array.isArray(pendingStake) && pendingStake.length > 0) {
          const pending = pendingStake[0];
          return formatTokenAmount(pending);
        }
        // If it's an object with pending property
        else if (typeof pendingStake === 'object' && pendingStake !== null) {
          const pending = (pendingStake as any).pending;
          return pending ? formatTokenAmount(pending.toString()) : '0';
        }
      }
      
      // Fallback to userStats if available
      if (userStats && typeof userStats === 'object') {
        // Array format [depositedUSD, pending, staked, ...]
        if (Array.isArray(userStats) && userStats.length > 1) {
          return formatTokenAmount(userStats[1]); // pending is at index 1
        }
        // Object format with tokensPending property
        else if ('tokensPending' in userStats && typeof (userStats as any).tokensPending !== 'undefined') {
          const pendingValue = (userStats as any).tokensPending;
          if (typeof pendingValue === 'string' || typeof pendingValue === 'number' || typeof pendingValue === 'bigint') {
            return formatTokenAmount(pendingValue.toString());
          }
        }
      }
      
      return '0';
    }
    
    // For ETH network - simple case
    return formatTokenAmount(pendingStake);
  };

  // Fetch Solana data from Supabase once when component mounts
  useEffect(() => {
    const fetchSolanaData = async () => {
      if (!user) return;
      
      setIsLoadingSolanaStake(true);
      try {
        console.log('Fetching Solana data from Supabase for address:', user);
        
        // First query to get all user purchases
        const { data: purchasesData, error: purchasesError } = await supabase
          .from('solana_purchases')
          .select('*')
          .eq('user_address', user);
          
        if (purchasesError) {
          console.error('Supabase error fetching purchases:', purchasesError);
          setIsLoadingSolanaStake(false);
          return;
        }
        
        console.log('Supabase returned purchase data:', purchasesData);
        
        // Now query the staking table to get staking information
        const { data: stakingData, error: stakingError } = await supabase
          .from('solana_staking')
          .select('*')
          .eq('user_address', user);
          
        if (stakingError) {
          console.error('Supabase error fetching staking data:', stakingError);
          // Continue with purchases data only
        }
        
        console.log('Supabase returned staking data:', stakingData);
        
        // Process the returned data
        if (purchasesData && purchasesData.length > 0) {
          // Calculate total tokens purchased from purchases table
          const totalPurchased = purchasesData.reduce((sum, item) => 
            sum + (parseFloat(item.token_amount) || 0), 0);
          
          // Calculate staked tokens from staking table if available
          let stakedTokens = 0;
          if (stakingData && stakingData.length > 0) {
            stakedTokens = stakingData.reduce((sum, item) => 
              sum + (parseFloat(item.staked_amount) || 0), 0);
          }
          
          // Calculate pending (unstaked) tokens
          const pending = Math.max(0, totalPurchased - stakedTokens);
          
          console.log('Processed Solana data -', {
            totalPurchased,
            stakedTokens,
            pending
          });
          
          setSolanaUserStaked(stakedTokens.toString());
          setSolanaPendingStake(pending.toString());
        } else {
          console.log('No Solana purchase data found for this user');
          setSolanaUserStaked('0');
          setSolanaPendingStake('0');
        }
      } catch (err) {
        console.error('Failed to fetch Solana data:', err);
      } finally {
        setIsLoadingSolanaStake(false);
      }
    };
    
    // Only fetch if we're dealing with a Solana wallet
    if (isSolanaWallet) {
      console.log('Detected Solana wallet, fetching data for address:', user);
      fetchSolanaData();
    } else {
      console.log('Not a Solana wallet, skipping Supabase fetch');
    }
  }, [user, isSolanaWallet]);

  // Refresh data on new blocks
  useEffect(() => {
    if (blockNumber) {
      refetchStats();
    }
  }, [blockNumber, refetchStats]);

  // Read total staked from ETH network
  const { data: ethTotalStaked } = useReadContract({
    address: contractAddress.eth,
    abi: abi,
    functionName: 'totalStaked',
    query: {
      enabled: true,
    }
  });

  // Read total staked from BSC network
  const { data: bscTotalStaked } = useReadContract({
    address: contractAddress.bsc,
    abi: abiBSC,
    functionName: 'totalStaked',
    query: {
      enabled: true,
    }
  });

  // Read pending staking reward
  const { data: pendingReward } = useReadContract({
    address: stakingContract,
    abi: networkKey === 'bsc' ? abiBSC : abi,
    functionName: 'getPendingStakingReward',
    args: [user ?? '0x0000000000000000000000000000000000000000'],
    query: {
      enabled: !!stakingContract && !!user,
    }
  }) as { data: bigint | undefined };

  // Fetch Solana total staked from Supabase
  useEffect(() => {
    const fetchSolanaTotalStaked = async () => {
      setIsLoadingTotals(true);
      try {
        // Query all staked tokens from Solana purchases
        const { data, error } = await supabase
          .from('solana_purchases')
          .select('token_amount')
          .eq('is_staked', true);
          
        if (error) {
          console.error('Error fetching Solana staked data:', error);
          setIsLoadingTotals(false);
          return;
        }
        
        // Calculate total staked
        const totalStaked = data.reduce((sum, purchase) => sum + (purchase.token_amount || 0), 0);
        setTotalStakedSOL(totalStaked.toString());
        
        console.log('Solana total staked loaded:', totalStaked);
        setIsLoadingTotals(false);
      } catch (error) {
        console.error('Failed to fetch Solana staking data:', error);
        setIsLoadingTotals(false);
      }
    };
    
    fetchSolanaTotalStaked();
  }, []);

  // Process ETH total staked
  useEffect(() => {
    if (ethTotalStaked) {
      try {
        // Convert and format ETH total staked
        const formattedAmount = formatTokenAmount(ethTotalStaked.toString());
        setTotalStakedETH(formattedAmount);
        console.log('ETH total staked loaded:', formattedAmount);
      } catch (error) {
        console.error('Error processing ETH total staked:', error);
      }
    }
  }, [ethTotalStaked]);

  // Process BSC total staked
  useEffect(() => {
    if (bscTotalStaked) {
      try {
        // Convert and format BSC total staked
        const formattedAmount = formatTokenAmount(bscTotalStaked.toString());
        setTotalStakedBSC(formattedAmount);
        console.log('BSC total staked loaded:', formattedAmount);
      } catch (error) {
        console.error('Error processing BSC total staked:', error);
      }
    }
  }, [bscTotalStaked]);

  // Calculate combined total
  useEffect(() => {
    const ethValue = parseFloat(totalStakedETH) || 0;
    const bscValue = parseFloat(totalStakedBSC) || 0;
    const solValue = parseFloat(totalStakedSOL) || 0;

    const total = ethValue + bscValue + solValue;
    setCombinedTotalStaked(total.toFixed(2));
    
    console.log('Combined total staked calculated:', {
      eth: ethValue,
      bsc: bscValue,
      sol: solValue,
      total: total
    });
  }, [totalStakedETH, totalStakedBSC, totalStakedSOL]);

  // Write: stake pending tokens
  const { writeContract: stakeTxn, data: stakeHash, isPending: isStaking } = useWriteContract();

  const handleStake = () => {
    stakeTxn({
      address: stakingContract,
      abi: networkKey === 'bsc' ? abiBSC : abi,
      functionName: 'stakePendingTokens',
      args: []
    });
  };

  const { isSuccess: stakeSuccess } = useWaitForTransactionReceipt({ 
    hash: stakeHash, 
    confirmations: 1 
  });

  // Write: unstake tokens
  const { writeContract: unstakeTxn, data: unstakeHash, isPending: isUnstaking } = useWriteContract();

  const handleUnstake = () => {
    unstakeTxn({
      address: stakingContract,
      abi: networkKey === 'bsc' ? abiBSC : abi,
      functionName: 'unstakeTokens',
      args: [userStats?.tokensStaked ?? 0]
    });
  };

  const { isSuccess: unstakeSuccess } = useWaitForTransactionReceipt({ 
    hash: unstakeHash, 
    confirmations: 1 
  });

  // Write: claim rewards
  const { writeContract: claimTxn, data: claimHash, isPending: isClaiming } = useWriteContract();

  const handleClaim = () => {
    claimTxn({
      address: stakingContract,
      abi: networkKey === 'bsc' ? abiBSC : abi,
      functionName: 'claimStakingRewards',
      args: []
    });
  };

  const { isSuccess: claimSuccess } = useWaitForTransactionReceipt({ 
    hash: claimHash, 
    confirmations: 1 
  });

  // On success, refresh stats
  useEffect(() => {
    if (stakeSuccess || unstakeSuccess || claimSuccess) {
      refetchStats();
    }
  }, [stakeSuccess, unstakeSuccess, claimSuccess, refetchStats]);

  // Generate monthly staking rewards data with cumulative values
  const generateMonthlyRewardsData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get current month index (0-11)
    const currentMonthIndex = new Date().getMonth();
    
    // Reorder months to start from current month
    const reorderedMonths = [
      ...months.slice(currentMonthIndex),
      ...months.slice(0, currentMonthIndex)
    ];
    
    // Monthly rate is approximately 10% (120% / 12)
    const monthlyRate = 10;
    
    // Return cumulative APY by month (showing growth toward 120% annual)
    return reorderedMonths.map((month, index) => {
      // Calculate cumulative APY (10% per month * number of months)
      // For month 1: 10%, month 2: 20%, month 3: 30%, etc.
      const cumulativeApy = monthlyRate * (index + 1);
      
      return {
        month,
        apy: Math.min(cumulativeApy, 120) // Cap at 120% annual rate
      };
    });
  };

  // Dynamic APY data for chart
  const stakingData = generateMonthlyRewardsData();

  return (
    <div className="py-6 md:py-10 px-4 sm:px-6 lg:px-8 w-full relative">
      <div className="max-w-7xl mx-auto relative">
        {/* Add glitchtail.gif aligned with containers */}
        <div className="absolute top-[-43px] md:top-[-89px] right-0 w-[150px] md:w-[200px] pointer-events-none md:z-[-1] z-[-1]">
          <img 
            src="/gif/glitchtail.gif" 
            alt="Glitchtail" 
            className="w-full h-auto" 
          />
        </div>
        
        {/* HEADER */}
        <div className="text-center mb-8 relative">
          <h2 className="font-tokyo text-3xl font-bold bg-gradient-to-r from-quantum-primary to-quantum-secondary bg-clip-text text-transparent mb-2">
            Stake & Earn
          </h2>
          <p className="text-sm text-quantum-light/80">
            Stake your $QGEN and earn up to <span className="text-quantum-primary font-semibold">120% APY</span>.
          </p>
        </div>

        {/* CARD ROW */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* STAKE */}
          <div className="quantum-card p-4 flex flex-col justify-between">
            <div>
              <p className="text-xs text-quantum-light/60 mb-1">Staked Balance</p>
              {isLoadingUserStats || isLoadingStakingBalance || isLoadingSolanaStake ? (
                <p className="font-mono text-sm mb-3">Loading...</p>
              ) : (
                <p className="font-mono text-sm mb-3">{getUserStakedAmount()} $QGEN</p>
              )}
              <p className="text-xs text-quantum-light/60 mb-1">Your Stakeable</p>
              {isLoadingPendingStake ? (
                <p className="font-mono text-sm mb-4">Loading...</p>
              ) : (
                <p className="font-mono text-sm mb-4">{getUserPendingAmount()} $QGEN</p>
              )}
            </div>
            <button
              onClick={handleStake}
              disabled={true} // Always disabled
              className="quantum-button w-full py-2 text-sm mt-auto opacity-50 cursor-not-allowed"
            >
              Stake Now
            </button>
          </div>

          {/* WITHDRAW */}
          <div className="quantum-card p-4 flex flex-col justify-between">
            <div>
              <p className="text-xs text-quantum-light/60 mb-1">% of Pool</p>
              <p className="font-mono text-sm mb-3">{userStats?.poolPercentage ?? '0'}%</p>
              <p className="text-xs text-quantum-light/60 mb-1">Total Staked</p>
              {isLoadingTotals ? (
                <p className="font-mono text-sm mb-4">Loading...</p>
              ) : (
                <p className="font-mono text-sm mb-4">{combinedTotalStaked} $QGEN</p>
              )}
            </div>
            <button
              onClick={handleUnstake}
              disabled={isUnstaking || (userStats?.tokensStaked ?? 0) === 0}
              className="quantum-button w-full py-2 text-sm mt-auto"
            >
              {isUnstaking ? 'Unstaking...' : 'Withdraw Staked Tokens'}
            </button>
          </div>

          {/* CLAIM */}
          <div className="quantum-card p-4 flex flex-col justify-between">
            <div>
              <p className="text-xs text-quantum-light/60 mb-1">Total Rewards</p>
              <p className="font-mono text-sm mb-4">{formatTokenAmount(pendingReward)} $QGEN</p>
            </div>
            <button
              onClick={handleClaim}
              disabled={true} // Always disabled
              className="quantum-button w-full py-2 text-sm mt-auto opacity-50 cursor-not-allowed"
            >
              Claim Rewards
            </button>
          </div>

          {/* ESTIMATED */}
          <div className="quantum-card p-4 flex flex-col justify-between">
            <div>
              <p className="text-xs text-quantum-light/60 mb-1">Estimated Rewards</p>
              <p className="font-mono text-sm mb-3">120% p/a</p>
              <ul className="text-xs text-quantum-light/60 space-y-1">
                <li>• Rewards rate is dynamic</li>
                <li>• Monthly = Rewards % / 12</li>
                <li>• Daily = Rewards % / 365</li>
              </ul>
            </div>
          </div>
        </div>

        {/* GRAPH */}
        <div className="quantum-card p-4">
          <h3 className="text-lg font-semibold text-quantum-light mb-4 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" /> Annual APY Growth (%)
          </h3>
          <div className="h-48 md:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stakingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="month" stroke="rgba(255,255,255,0.5)" />
                <YAxis stroke="rgba(255,255,255,0.5)" domain={[0, 130]} />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'rgba(15,23,42,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                  formatter={(value) => [`${value}%`, 'Cumulative APY']}
                />
                <Line type="monotone" dataKey="apy" strokeWidth={2} dot={{ fill: '#8B5CF6' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-quantum-light/60 text-center mt-2">
            Grows by 10% monthly, reaching the full 120% annual APY after 12 months
          </div>
        </div>
      </div>
    </div>
  );
};

export default Staking;
